import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { TaxTypeFiltersType, TaxTypeType, PaginationType } from './types.ts'
import Form from './form.tsx'
import {Button} from "@/components/ui/button.tsx";
import { DollarSign, Plus, Search, Trash2, X } from "lucide-react";
import {Card} from "@/components/ui/card.tsx";
import {Input} from "@/components/ui/input.tsx";
import Pagination from "@/components/pagination.tsx";

interface IndexProps {
  tax_types: TaxTypeType[]
  filters: TaxTypeFiltersType
  pagination: PaginationType
  can_create: boolean
  can_destroy?: boolean
  stats: {
    total_tax_types: number
    active_tax_types: number
    total_collected: number
  }
}

export default function Index({ tax_types, filters, pagination, can_create, can_destroy = false, stats }: IndexProps) {
  const hasInitialized = useRef(false)
  const [term, setTerm] = useState(filters.term)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const emptyTaxType: TaxTypeType = {
    id: "",
    name: "",
    code: "",
    description: "",
    active: false,
    community_id: ""
  }

  useEffect(() => {
    setTerm(filters.term)
  }, [filters.term])

  const query = useMemo(() => ({ term }), [term])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (term === filters.term) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get("/admin/tax_types", { ...query, page: 1 }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [query, term, filters.term])

  const handleDestroy = (taxType: TaxTypeType) => {
    if (!window.confirm(`Supprimer le type de taxe "${taxType.name}" ?`)) return

    router.delete(`/admin/tax_types/${taxType.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title="Types de taxe" />
      <div className="p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Les types de taxe</h2>
            <p className="text-muted-foreground">Gérer les différents types de taxe</p>
          </div>
          {can_create && (
            <Button className="gap-2" onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="h-4 w-4" />
                Ajouter un type de taxe
            </Button>
          )}
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-3">
          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Nombre total de types</p>
                <p className="text-2xl font-bold text-foreground">{stats?.total_tax_types ?? 0}</p>
              </div>
            </div>
          </Card>

          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-success/10 p-2">
                <DollarSign className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Types de taxe actifs</p>
                <p className="text-2xl font-bold text-foreground">{stats?.active_tax_types ?? 0}</p>
              </div>
            </div>
          </Card>

          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-accent/10 p-2">
                <DollarSign className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Montant total collecté</p>
                <p className="text-xl font-bold text-foreground">{(stats?.total_collected ?? 0).toLocaleString()} FCFA</p>
              </div>
            </div>
          </Card>
        </div>

        <Card className="border-border bg-card p-6">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                  placeholder="Rechercher un type de taxe par nom, code ou description..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
              <tr className="border-b border-border text-left text-sm font-medium text-muted-foreground">
                <th className="pb-3 pr-4">Code</th>
                <th className="pb-3 pr-4">Type de taxe</th>
                <th className="pb-3 pr-4">Contribuables payeurs</th>
                <th className="pb-3 pr-4">Montant collecté</th>
                <th className="pb-3 pr-4">Statut</th>
                <th className="pb-3">Actions</th>
              </tr>
              </thead>
              <tbody>
              {tax_types.map((type) => (
                  <tr key={type.id} className="border-b border-border text-sm">
                    <td className="py-3 pr-4 font-medium text-foreground">{type.code}</td>
                    <td className="py-3 pr-4 font-mono text-foreground">{type.name}</td>
                    <td className="py-3 pr-4 font-mono font-semibold text-foreground">
                      {type.total_tax_payers_paying ?? 0}
                    </td>
                    <td className="py-3 pr-4 font-mono font-semibold text-foreground">
                      {(type.total_amount_collected ?? 0).toLocaleString()} FCFA
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${type.active ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                        {type.active ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="py-3">
                      <Link href={`/admin/tax_types/${type.id}`} className="mr-2">
                        <button className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">Détails</button>
                      </Link>
                      {can_destroy && (
                        <button
                          type="button"
                          className="inline-flex items-center rounded-md p-2 text-destructive hover:bg-destructive/10"
                          aria-label={`Supprimer ${type.name}`}
                          onClick={() => handleDestroy(type)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="mt-6">
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            basePath="/admin/tax_types"
            query={query}
          />
        </div>
      </div>

      {can_create && isCreateModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4 backdrop-blur-sm"
          onClick={() => setIsCreateModalOpen(false)}
        >
          <div
            className="w-full max-w-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-semibold text-foreground">Ajouter un type de taxe</h3>
                <p className="mt-1 text-sm text-muted-foreground">Renseignez les informations du nouveau type de taxe.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="bg-transparent"
                onClick={() => setIsCreateModalOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <Form
              tax_type={emptyTaxType}
              method="post"
              action="/admin/tax_types"
              submitText="Créer le type de taxe"
              onSuccess={() => setIsCreateModalOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  )
}
